import { CLASS_HUES, MAX_ROOM_SIZE, MIN_ROOM_SIZE, PARTNER_GROUPS, ROOM_FRONTS } from './config.js';

function read(key, fallback) {
  try {
    const value = JSON.parse(localStorage.getItem(key))
    return value === null || value === undefined ? fallback : value
  } catch (error) {
    return fallback
  }
}

function write(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error(`Error storing ${key}:`, error);
  }
}

export function getLastSeenVersion() {
  try {
    return localStorage.getItem('last_seen_version') || null;
  } catch (error) {
    return null;
  }
}
export function setLastSeenVersion(version) {
  try {
    localStorage.setItem('last_seen_version', version);
  } catch (error) {
    console.error('Error storing version:', error);
  }
}

// Older versions stored a seat's student on the table and had no class hue.
function normalizeClass(cla, position) {
  const hueKeys = CLASS_HUES.map(h => h.key)
  return {
    id: String(cla.id),
    name: String(cla.name || 'Klasse'),
    hue: hueKeys.includes(cla.hue) ? cla.hue : hueKeys[position % hueKeys.length],
    students: (Array.isArray(cla.students) ? cla.students : [])
      .filter(student => student && student.id && student.name)
      .map(student => ({
        id: String(student.id),
        name: String(student.name),
        blockingGroup: [1, 2, 3].includes(student.blockingGroup) ? student.blockingGroup : 0,
        partnerGroup: student.partnerGroup >= 1 && student.partnerGroup <= PARTNER_GROUPS ? Math.round(student.partnerGroup) : 0,
      })),
  }
}

function normalizeRoom(room) {
  const clamp = n => Math.max(MIN_ROOM_SIZE, Math.min(MAX_ROOM_SIZE, Math.round(Number(n)) || 0))
  const source = Array.isArray(room.tables) ? room.tables : []
  const sourceCols = Math.max(1, Math.round(Number(room.cols)) || 10)
  const cols = clamp(sourceCols)
  const rows = clamp(Math.ceil(source.length / sourceCols) || room.rows || 8)
  const tables = []
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const old = source[r * sourceCols + c]
      tables.push({ index: r * cols + c, available: !!(old && old.available) })
    }
  }
  // Rooms from before the board marker don't say where it is, so they show none
  const front = ROOM_FRONTS.some(f => f.key === room.front) ? room.front : 'none'
  return { id: String(room.id), name: String(room.name || 'Zimmer'), rows, cols, front, tables }
}

function normalizePlans(plans) {
  const clean = {}
  if (!plans || typeof plans !== 'object') return clean
  Object.entries(plans).forEach(([key, seats]) => {
    if (!seats || typeof seats !== 'object') return
    clean[key] = {}
    Object.entries(seats).forEach(([index, studentId]) => {
      if (typeof studentId === 'string' && studentId) clean[key][index] = studentId
    })
  })
  return clean
}

export function storeRooms(rooms) {
  write('rooms', rooms)
}
export function loadRooms() {
  const rooms = read('rooms', [])
  return Array.isArray(rooms) ? rooms.filter(r => r && r.id).map(normalizeRoom) : []
}
export function storeClasses(classes) {
  write('classes', classes)
}
export function loadClasses() {
  const classes = read('classes', [])
  return Array.isArray(classes) ? classes.filter(c => c && c.id).map(normalizeClass) : []
}

// Seating plans, one per class and room: { "classId|roomId": { tableIndex: studentId } }
export function planKey(classId, roomId) {
  return `${classId}|${roomId}`
}
export function storePlans(plans) {
  write('plans', plans)
}
export function loadPlans() {
  return normalizePlans(read('plans', {}))
}

// Fixed arrangements, built by hand and saved apart from the plan on show: { "classId|roomId": { tableIndex: studentId } }
export function storeFixedPlans(fixedPlans) {
  write('fixedPlans', fixedPlans)
}
export function loadFixedPlans() {
  return normalizePlans(read('fixedPlans', {}))
}

// Fixed seats: the students who keep their seat when a plan is shuffled, { "classId|roomId": [studentId] }
function normalizePins(pins) {
  const clean = {}
  if (!pins || typeof pins !== 'object') return clean
  Object.entries(pins).forEach(([key, studentIds]) => {
    if (Array.isArray(studentIds)) clean[key] = studentIds.filter(id => typeof id === 'string' && id)
  })
  return clean
}
export function storePins(pins) {
  write('pins', pins)
}
export function loadPins() {
  return normalizePins(read('pins', {}))
}

export function storeSelection(selection) {
  write('selection', selection)
}
export function loadSelection() {
  const selection = read('selection', {})
  return selection && typeof selection === 'object' ? selection : {}
}

export function exportData() {
  return {
    version: 2,
    classes: loadClasses(),
    rooms: loadRooms(),
    plans: loadPlans(),
    pins: loadPins(),
    fixedPlans: loadFixedPlans(),
  }
}

// Reads a backup file's content. Throws when it isn't one.
export function parseBackup(text) {
  const data = JSON.parse(text)
  if (!data || !Array.isArray(data.classes) || !Array.isArray(data.rooms)) {
    throw new Error('not a backup')
  }
  return {
    classes: data.classes.filter(c => c && c.id).map(normalizeClass),
    rooms: data.rooms.filter(r => r && r.id).map(normalizeRoom),
    plans: normalizePlans(data.plans),
    pins: normalizePins(data.pins),
    fixedPlans: normalizePlans(data.fixedPlans),
  }
}

export function importData(data) {
  storeClasses(data.classes)
  storeRooms(data.rooms)
  storePlans(data.plans)
  storePins(data.pins)
  storeFixedPlans(data.fixedPlans)
}
